import GenericLoading from '@cannect/components/GenericLoading'
import { formatReal } from '@cannect/utils/formatters'
import {
  FREE_NATIONAL_FREIGHT_LABEL,
  hasQuotedNationalFreight,
  shouldShowNationalFreight
} from '@cannect/utils/nationalFreight'
import LoadingContainer from 'components/LoadingContainer'
import { Separator } from 'new-components/atoms'
import { AwbsData, PackagesData } from 'pages/Account/components/pages/MyOrders/types'
import { useEffect, useState } from 'react'
import {
  BsArrowLeft,
  BsBoxSeam,
  // BsCardCheck,
  BsCheckCircle,
  BsCheckCircleFill,
  BsCreditCard,
  BsFileEarmarkCheck,
  BsFileEarmarkText,
  BsShieldCheck,
  BsTicketPerforated,
  BsTruck
} from 'react-icons/bs'
import { useHistory, useLocation, useParams } from 'react-router'
import { toast } from 'react-toastify'
import api from 'services/api'
import { useTokenApi } from 'hooks/useTokenApi'
import Header from './components/Header'
import ShippingDetails from './components/ShippingDetails'
import Timeline from './components/Timeline'
import * as S from './styles'
import { getProductById } from '@cannect/services/resources/products/requests'
import { setOrderId as setAssociationOrderId } from 'pages/AccountAssociations/components/pages/Cart/utils/orderStorage'

// Constantes para marcas que não precisam de inspeção ANVISA
const BRANDS_WITH_REQUIRED_FREIGHT = [177, 173] // Não precisam de ANVISA
const BRANDS_WITH_POPUP = [177] // Mostram pop-up do pacote enviado

// Associação usada no fluxo /AreaAssociados (mesmo id fixo usado nas demais telas da área)
const ASSOCIATION_ID = 1

interface OrderStatusType {
  color: string
  date: string
  status: string
  translated_name: string
}

const status = [
  {
    id: 1,
    idName: ['awaiting_payment', 'paid'],
    name: 'Status do pagamento.',
    checkedName: 'Pagamento aprovado',
    activeName: 'Aguardando confirmação do pagamento',
    icon: <BsCreditCard />,
    checked: false,
    isVerified: false,
    label: 'Aguardando confirmação do pagamento',
    nextStepName: ['waiting_documentation']
  },
  {
    id: 2,
    idName: ['waiting_documentation', 'waiting_approval', 'waiting_anvisa', 'disapproved_document'],
    name: 'Validações de documentos.',
    checkedName: 'Documentos aprovados',
    activeName: 'Documentos em validação',
    icon: <BsFileEarmarkCheck />,
    checked: false,
    isVerified: false,
    label: 'Documentos aprovados',
    nextStepName: ['approved_documents']
  },
  {
    id: 4,
    idName: [
      'dispatched',
      'created',
      'approved_documents',
      'approved',
      'waiting_separation',
      'in_separation',
      'preparing_shipping',
      'separated',
      'ready_shipping',
      'waiting_stock',
      'sented',
      'shipping_supplier'
    ],
    name: 'Status do pacote',
    checkedName: 'Pacote enviado',
    activeName: 'Pacote em preparação',
    icon: <BsBoxSeam />,
    checked: false,
    isVerified: false,
    label: 'Pacote enviado',
    nextStepName: ['dispatched', 'sented'],
    infoContent:
      'Seu pacote já foi preparado e coletado pela nossa transportadora parceira. Agora ele está a caminho do Brasil! Fique tranquilo, estamos monitorando cada etapa do trajeto até a chegada no país.'
  },
  {
    id: 5,
    idName: ['problems_with_customs'],
    name: 'Alfândega EUA',
    checkedName: 'Liberado pela Alfândega EUA',
    activeName: 'Em análise na Alfândega EUA',
    icon: <BsShieldCheck />,
    checked: false,
    isVerified: false,
    label: 'Alfândega EUA',
    nextStepName: ['release_anvisa', 'anvisa_exception', 'delay_release', 'anvisa_br', 'in_transit', 'delivered'],
    infoContent: `Seu pacote está passando por uma inspeção de rotina na alfândega dos Estados Unidos antes de seguir viagem para o Brasil.

Esse é um procedimento padrão e pode levar alguns dias úteis. Estamos acompanhando o processo e você será notificado assim que o pacote for liberado.

Se precisar de ajuda, entre em contato com nosso time de atendimento.`
  },
  // {
  //   id: 5,
  //   idName: ['anvisa_br'],
  //   name: 'Chegou no Brasil',
  //   checkedName: 'Chegou no Brasil',
  //   activeName: 'A caminho do Brasil',
  //   icon: <BsFlag />,
  //   checked: false,
  //   isVerified: false,
  //   label: 'Chegou no Brasil',
  //   nextStepName: ['anvisa_br']
  // },
  // {
  //   id: 6,
  //   idName: ['anvisa_exception', 'delay_release', 'anvisa_br'],
  //   name: 'Inspeção ANVISA',
  //   checkedName: 'Aprovado pela ANVISA',
  //   activeName: 'Em inspeção ANVISA',
  //   icon: <BsClipboardCheck />,
  //   checked: false,
  //   isVerified: false,
  //   label: 'Inspeção ANVISA',
  //   nextStepName: ['release_anvisa']
  // },
  {
    id: 7,
    idName: ['release_anvisa', 'anvisa_exception', 'delay_release', 'anvisa_br'],
    name: 'Liberado pela ANVISA',
    checkedName: 'Liberado pela ANVISA',
    activeName: 'Aguardando liberação',
    icon: <BsCheckCircle />,
    checked: false,
    isVerified: false,
    label: 'Liberado pela ANVISA',
    nextStepName: ['delivered', 'in_transit', 'not_delivered', 'out_for_delivery'],
    infoContent: `Seu pacote já chegou ao Brasil e agora está passando por uma inspeção da Receita Federal e da Anvisa, esse é um procedimento padrão para medicamentos importados.

Se for necessário apresentar alguma documentação adicional, nosso time entrará em contato com você diretamente.

Essa etapa pode levar alguns dias, mas não se preocupe: estamos acompanhando todo o processo de perto.

📌 Atenção:
Nunca cobramos taxas extras de importação ou liberação alfandegária.
Nossos parceiros também não fazem esse tipo de cobrança.
Pela legislação brasileira, medicamentos importados para uso próprio com valor de até US$ 10.000 são isentos de impostos.

Se tiver qualquer dúvida, nosso time de atendimento está à disposição para te ajudar.`
  },
  {
    id: 8,
    idName: ['delivered', 'in_transit', 'not_delivered', 'out_for_delivery'],
    name: 'Pedido entregue',
    checkedName: 'Pedido Entregue',
    activeName: 'Aguardando Entrega',
    icon: <BsTruck />,
    checked: false,
    isVerified: false,
    label: 'Pedido entregue',
    nextStepName: ['delivered'],
    infoContent: `Boa notícia! Seu pacote está a caminho da sua cidade.

Assim que ele chegar ao destino, será incluído na rota de entregas da transportadora.

📌 Importante:
Em algumas regiões — principalmente no Norte e Nordeste — pode ocorrer uma fiscalização adicional pela SEFAZ (Secretaria da Fazenda), o que pode causar um maior tempo de entrega.

Mas fique tranquilo, estamos acompanhando tudo de perto e informaremos qualquer novidade.

Se tiver dúvidas, entre em contato com nosso time de atendimento.`
  }
]

// Função para gerar mensagem descritiva baseada no status
function getStatusMessage(statusName: string, stepId: number, orderData?: any): string {
  let message = ''

  // Step 1: Pagamento
  if (stepId === 1) {
    message = 'Pagamento confirmado'
  }

  // Step 2: Documentos
  if (stepId === 2) {
    if (statusName === 'waiting_documentation') message = 'Aguardando envio dos documentos'
    if (statusName === 'waiting_approval')
      message = 'Recebemos todos os seus documentos, agora estamos gerando sua autorização Anvisa'
    if (statusName === 'waiting_anvisa')
      message = 'Recebemos todos os seus documentos, em breve entraremos em contato com mais informações'
    if (statusName === 'disapproved_document') message = 'Houve um problema com seus documentos'
    if (statusName === 'approved_documents') message = 'Tudo certo com seus documentos'
  }

  // Step 4: Pacote
  if (stepId === 4) {
    if (statusName === 'waiting_stock')
      message = 'Seu pedido está em etapa de preparação logística e em breve seguirá para envio.'
    if (
      statusName === 'created' ||
      statusName === 'approved' ||
      statusName === 'approved_documents' ||
      statusName === 'waiting_separation'
    ) {
      message = 'Seu pacote foi enviado para separação'
    }
    if (statusName === 'in_separation') message = 'Seu pacote está sendo separado'
    if (statusName === 'preparing_shipping' || statusName === 'separated' || statusName === 'ready_shipping') {
      message = 'Seu pacote foi embalado. Em breve será enviado para transportadora'
    }
    if (statusName === 'dispatched' || statusName === 'sented' || statusName === 'shipping_supplier') {
      message = 'Seu pacote já foi enviado.'
    }
  }

  // Step 5: Alfândega EUA
  if (stepId === 5) {
    if (statusName === 'problems_with_customs') {
      message = 'Seu pacote está em análise na alfândega dos EUA.'
    }
  }

  // Step 7: ANVISA
  if (stepId === 7) {
    if (statusName === 'anvisa_br' || statusName === 'anvisa_exception' || statusName === 'delay_release') {
      message = 'Seu pacote está em análise pela ANVISA.'
    }
    if (statusName === 'release_anvisa') {
      message = 'Seu pacote foi liberado pela ANVISA. Em breve entrará em rota de entrega'
    }
  }

  // Step 8: Entrega
  if (stepId === 8) {
    if (statusName === 'in_transit' || statusName === 'not_delivered') {
      message = orderData?.street
        ? `Seu pacote está em trânsito. Destino: ${orderData.street}, ${orderData.address_number}`
        : 'Seu pacote está em trânsito'
    }
    if (statusName === 'out_for_delivery') {
      message = orderData?.street
        ? `Seu pacote saiu para entrega. Destino: ${orderData.street}, ${orderData.address_number}`
        : 'Seu pacote saiu para entrega'
    }
    if (statusName === 'delivered') message = 'Seu pedido foi entregue'
  }

  return message
}

// Função para encontrar o log mais avançado na progressão do step (não apenas o mais recente por data)
// A ordem do array idName representa a progressão: índice maior = mais avançado
function getMostAdvancedLog(logs: any[], stepIdNames: string[]): any {
  if (!logs || logs.length === 0) return null

  // Filtra apenas logs que estão no idName do step
  const relevantLogs = logs.filter((log: any) => stepIdNames.includes(log.status?.name))
  if (relevantLogs.length === 0) return null

  // Encontra o log com o status mais avançado (maior índice no array idName)
  return relevantLogs.reduce((mostAdvanced: any, log: any) => {
    if (!mostAdvanced) return log

    const currentIndex = stepIdNames.indexOf(log.status?.name)
    const mostAdvancedIndex = stepIdNames.indexOf(mostAdvanced.status?.name)

    if (currentIndex > mostAdvancedIndex) {
      return log
    }
    return mostAdvanced
  }, null)
}

// Nomes de status que representam eventos do pedido (não do pacote específico)
// Esses logs vêm mesclados no endpoint package-with-status mas se aplicam ao pedido inteiro
const ORDER_LEVEL_STATUS_NAMES = [
  'awaiting_payment',
  'paid',
  'waiting_documentation',
  'waiting_approval',
  'waiting_anvisa',
  'disapproved_document',
  'approved_documents'
]

// Filtra os statusLogs mantendo apenas os logs pertinentes ao pacote:
// - logs com package_id == id do pacote
// - logs de eventos de nível de pedido (documentos, pagamento) — que se aplicam a todos os pacotes
function filterLogsForPackage(logs: any[] | undefined, pkg: any): any[] {
  if (!Array.isArray(logs) || logs.length === 0) return []
  const packageId = pkg?.id ?? pkg?.package_id
  return logs.filter((log: any) => {
    const logPackageId = log?.package_id
    if (logPackageId != null && packageId != null) {
      return logPackageId === packageId
    }
    // Log sem package_id → é de order_status_log; só considerar se for status de nível de pedido
    return ORDER_LEVEL_STATUS_NAMES.includes(log?.status?.name)
  })
}

// Escolhe o status "efetivo" do pacote pegando o log mais avançado dentro da progressão
// definida pelos steps. Se não achar match em nenhum step, cai no latest por updatedAt.
function getEffectivePackageStatus(logs: any[], stepsProgression: string[], fallback: any): any {
  if (!Array.isArray(logs) || logs.length === 0) return fallback

  let bestLog: any = null
  let bestIndex = -1

  for (const log of logs) {
    const idx = stepsProgression.indexOf(log?.status?.name)
    if (idx > bestIndex) {
      bestIndex = idx
      bestLog = log
    }
  }

  return bestLog?.status || fallback
}

// Steps alternativos para pedidos 100% TESTE_GENETICO
const GENETIC_TEST_STATUS = [
  {
    id: 'gt_1',
    idName: ['awaiting_payment', 'paid'],
    name: 'Criado',
    checkedName: 'Pagamento aprovado',
    activeName: 'Aguardando pagamento',
    icon: <BsCreditCard />,
    nextStepName: ['sented', 'dispatched']
  },
  {
    id: 'gt_2',
    idName: ['sented', 'dispatched'],
    name: 'Enviado',
    checkedName: 'Enviado',
    activeName: 'Enviado',
    icon: <BsTruck />,
    nextStepName: ['kit_activated']
  },
  {
    id: 'gt_3',
    idName: ['kit_activated'],
    name: 'Kit ativado',
    checkedName: 'Kit ativado',
    activeName: 'Kit ativado',
    icon: <BsCheckCircle />,
    nextStepName: ['received']
  },
  {
    id: 'gt_4',
    idName: ['received'],
    name: 'Recebido',
    checkedName: 'Recebido',
    activeName: 'Recebido',
    icon: <BsBoxSeam />,
    nextStepName: ['under_review']
  },
  {
    id: 'gt_5',
    idName: ['under_review'],
    name: 'Em análise',
    checkedName: 'Em análise',
    activeName: 'Em análise',
    icon: <BsFileEarmarkCheck />,
    nextStepName: ['partially_completed', 'completed']
  },
  {
    id: 'gt_6',
    idName: ['partially_completed'],
    name: 'Concluído Parcialmente',
    checkedName: 'Concluído Parcialmente',
    activeName: 'Concluído Parcialmente',
    icon: <BsCheckCircle />,
    nextStepName: ['completed']
  },
  {
    id: 'gt_7',
    idName: ['completed'],
    name: 'Concluído',
    checkedName: 'Concluído',
    activeName: 'Concluído',
    icon: <BsCheckCircleFill />,
    nextStepName: []
  }
]

const DOCUMENT_REFUSED_LABEL = 'Houve um problema com seus documentos'

const PILL_ORDER_STATUS = [
  {
    id: 'pill_1',
    idName: ['awaiting_payment', 'paid'],
    name: 'Pagamento confirmado',
    checkedName: 'Pagamento confirmado',
    activeName: 'Aguardando confirmação do pagamento',
    icon: <BsCreditCard />,
    nextStepName: ['waiting_documentation', 'approved']
  },
  {
    id: 'pill_2',
    idName: ['waiting_documentation', 'waiting_approval', 'disapproved_document'],
    name: 'Documentos',
    checkedName: 'Documentos aprovados',
    activeName: 'Documentos em validação',
    icon: <BsFileEarmarkCheck />,
    nextStepName: ['approved']
  },
  {
    id: 'pill_3',
    idName: ['approved'],
    name: 'Preparando envio',
    checkedName: 'Pedido preparado',
    activeName: 'Preparando envio',
    icon: <BsBoxSeam />,
    nextStepName: ['dispatched', 'sented']
  },
  {
    id: 'pill_4',
    idName: ['dispatched', 'sented'],
    name: 'Enviado',
    checkedName: 'Enviado',
    activeName: 'Enviado',
    icon: <BsTruck />,
    nextStepName: ['delivered']
  },
  {
    id: 'pill_5',
    idName: ['delivered'],
    name: 'Entregue',
    checkedName: 'Entregue',
    activeName: 'Entregue',
    icon: <BsCheckCircleFill />,
    nextStepName: []
  }
]

function buildPillOrderTimeline(statusLogs: any[], paymentStatus: any, hasControlledItem?: boolean) {
  const steps = hasControlledItem ? PILL_ORDER_STATUS : PILL_ORDER_STATUS.filter((s) => s.id !== 'pill_2')

  const allLaterIdNames = (fromIndex: number) => steps.slice(fromIndex + 1).flatMap((s) => s.idName)

  return steps.map((step, index) => {
    let statusCreatedAt = ''
    let shouldBeChecked = false
    let itemLabel = step.name
    let refused = false

    if (step.id === 'pill_1') {
      const isPaid = paymentStatus?.status === 'paid'
      shouldBeChecked = isPaid
      itemLabel = isPaid ? step.checkedName : step.activeName
      if (isPaid) statusCreatedAt = paymentStatus?.createdAt || ''
    } else {
      const logsForStep = (statusLogs || []).filter((log: any) => step.idName.includes(log.status?.name))
      const hasLaterStep = (statusLogs || []).some((log: any) => allLaterIdNames(index).includes(log.status?.name))
      const latestLog = [...logsForStep].sort(
        (a: any, b: any) =>
          new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
      )[0]

      refused = step.id === 'pill_2' && latestLog?.status?.name === 'disapproved_document'
      const awaitingDocumentDecision =
        step.id === 'pill_2' &&
        (latestLog?.status?.name === 'waiting_documentation' || latestLog?.status?.name === 'waiting_approval')

      if (refused) {
        itemLabel = DOCUMENT_REFUSED_LABEL
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      } else if (awaitingDocumentDecision) {
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      } else if (hasLaterStep) {
        shouldBeChecked = true
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      } else if (logsForStep.length > 0) {
        shouldBeChecked = allLaterIdNames(index).length === 0
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      }
    }

    return {
      ...step,
      activeName: refused ? DOCUMENT_REFUSED_LABEL : step.activeName,
      checked: shouldBeChecked,
      isVerified: shouldBeChecked,
      label: itemLabel,
      refused,
      statusCreatedAt,
      message: ''
    }
  })
}

function buildGeneticTestTimeline(statusLogs: any[], paymentStatus: any) {
  // Todos os status names de steps posteriores a cada índice
  const allLaterIdNames = (fromIndex: number) => GENETIC_TEST_STATUS.slice(fromIndex + 1).flatMap((s) => s.idName)

  return GENETIC_TEST_STATUS.map((step, index) => {
    let statusCreatedAt = ''
    let shouldBeChecked = false
    let itemLabel = step.name

    if (step.id === 'gt_1') {
      const isPaid = paymentStatus?.status === 'paid'
      shouldBeChecked = isPaid
      itemLabel = isPaid ? 'Pagamento aprovado' : 'Aguardando pagamento'
      if (isPaid) statusCreatedAt = paymentStatus?.createdAt || ''
    } else {
      const logsForStep = (statusLogs || []).filter((log: any) => step.idName.includes(log.status?.name))
      // Step está superado se há log de qualquer step posterior na sequência
      const hasLaterStep = (statusLogs || []).some((log: any) => allLaterIdNames(index).includes(log.status?.name))

      if (hasLaterStep) {
        shouldBeChecked = true
        const latestLog = [...logsForStep].sort(
          (a: any, b: any) =>
            new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
        )[0]
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      } else if (logsForStep.length > 0) {
        const isLastStep = allLaterIdNames(index).length === 0
        shouldBeChecked = isLastStep
        const latestLog = [...logsForStep].sort(
          (a: any, b: any) =>
            new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime()
        )[0]
        statusCreatedAt = latestLog?.updatedAt || latestLog?.createdAt || ''
      }
    }

    return {
      ...step,
      checked: shouldBeChecked,
      isVerified: shouldBeChecked,
      label: itemLabel,
      statusCreatedAt,
      message: ''
    }
  })
}

const RECEIPT_LABELS: Record<string, string> = {
  payment: 'Comprovante de pagamento',
  refund: 'Comprovante de estorno',
  complement: 'Comprovante de cobrança complementar'
}

interface OrderReceipt {
  id: string
  type: 'payment' | 'refund' | 'complement'
  order_id: number
  file_name: string
  download_url: string
}

function OrderDetails() {
  const [orderDetails, setOrderDetails] = useState<any>({ items: [] })
  const [PackageDetails, setPackageDetails] = useState<any>({ packages: [] })
  const [orderStatus, setOrderStatus] = useState({} as OrderStatusType)
  const [timeLineStatus, setTimeLineStatus] = useState(status)
  const [loading, setLoading] = useState(false)
  const { cart_id } = useParams() as any
  const history = useHistory()
  const { search } = useLocation()
  const isWaFlowFromUrl = new URLSearchParams(search).get('wa') === '1'
  const { tokenApi } = useTokenApi()
  const isLoggedIn = !!tokenApi
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(null)
  const [OrderPackage, setOrderPackage] = useState<PackagesData[]>([])
  const [OrderAwb, setOrderAwb] = useState<AwbsData[]>([])
  const [PackageCount, setPackageCount] = useState<number>(0)
  const [orderReceipts, setOrderReceipts] = useState<OrderReceipt[]>([])
  const isSmallScreen = window.innerWidth < 750
  const [Data, SetData] = useState<any>({})
  const [StatusIndex, setStatusIndex] = useState<number>(0)
  const [isDeliveredByAssociation, setIsDeliveredByAssociation] = useState(false)
  const [orderId, setOrderId] = useState<string>('')
  const [reorderLoading, setReorderLoading] = useState(false)
  const isAssociationRoute = window.location.pathname.includes('/AreaAssociados')
  const linkAtendimento = isAssociationRoute
    ? 'https://api.whatsapp.com/send/?phone=5575999900420&text&type=phone_number&app_absent=0'
    : 'https://api.whatsapp.com/send/?phone=5511982711992&text=Oi%2C+gostaria+de+tirar+dúvidas++sobre+meu+pedido%21&type=phone_number&app_absent=0'

  const handleBackToAssociationArea = () => {
    history.push('/AreaAssociados/MinhaConta?pagina=pedidos-todos')
  }

  // Seleciona o log mais recente considerando updatedAt (fallback em createdAt)
  const getLatestByUpdated = (logs: any[] | undefined) => {
    if (!Array.isArray(logs) || logs.length === 0) return undefined
    return logs.reduce((latest: any, log: any) => {
      if (!latest) return log
      const latestUp = new Date(log?.updatedAt || log?.createdAt).getTime()
      const currentUp = new Date(latest?.updatedAt || latest?.createdAt).getTime()
      return latestUp > currentUp ? log : latest
    })
  }

  const getLastOrders = async (loading = true) => {
    setLoading(loading)
    try {
      let data: any = null
      if (!isLoggedIn) {
        const resPublic = await api.get(`/order_status_public/${cart_id}`)
        data = resPublic.data
      } else {
        try {
          const res = await api.post(`/order_status/${cart_id}`)
          data = res.data
        } catch (err: any) {
          if (err?.response?.status === 404) {
            const resPublic = await api.get(`/order_status_public/${cart_id}`)
            data = resPublic.data
          } else {
            throw err
          }
        }
      }
      if (data?.order) {
        const responsePackage = await api.get(`/packages/${data?.order?.id}/package-with-status`)
        const statusIndex = status.findIndex((item) => item.idName.includes(data?.order?.status?.order_status?.status))
        const packageDetails = responsePackage?.data?.packages
        const currentPackage = packageDetails?.[PackageCount]
        const packageLogs = filterLogsForPackage(currentPackage?.statusLogs, currentPackage)
        const stepsProgression = status.flatMap((s) => s.idName)
        const effectiveStatus = getEffectivePackageStatus(
          packageLogs,
          stepsProgression,
          data?.order?.status?.order_status
        )
        let orderStatus = statusIndex > 1 ? effectiveStatus : data?.order?.status?.order_status

        const matchingLogForEffective = packageLogs.find((log: any) => log?.status?.name === orderStatus?.name)
        orderStatus = {
          ...orderStatus,
          statusData:
            matchingLogForEffective?.updatedAt ||
            matchingLogForEffective?.createdAt ||
            data?.order?.status?.order_status?.updatedAt
        }

        // Verifica se todos os produtos não precisam de ANVISA
        const currentPackageItems = packageDetails[PackageCount]?.packages_items || []
        const currentPackageSupplierId = currentPackageItems[0]?.supplier_id

        // Verifica produtos que não precisam de ANVISA por delivery_by_association, class_id ou supplier
        const allProductsDeliveredByAssociation = currentPackageItems.every(
          (packageItem: any) =>
            packageItem.product?.delivery_by_association === true ||
            packageItem.class_id === 4 ||
            packageItem.class_id === 6
        )

        // Verifica produtos que não precisam de ANVISA por brand_id
        const allProductsWithSpecialBrand = currentPackageItems.every(
          (packageItem: any) =>
            BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.brand_id) ||
            BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.product?.brand_id)
        )

        // Verifica se todos os produtos têm brand_id que deve mostrar pop-up (apenas 177)
        const allProductsWithPopupBrand = currentPackageItems.every(
          (packageItem: any) =>
            BRANDS_WITH_POPUP.includes(packageItem.brand_id) ||
            BRANDS_WITH_POPUP.includes(packageItem.product?.brand_id)
        )

        // Verifica se todos os produtos são de associação (brand_id 176)
        const allProductsAssociation = currentPackageItems.every(
          (packageItem: any) => packageItem.brand_id === 176 || packageItem.product?.brand_id === 176
        )

        // Combina todas as condições para remover ANVISA
        const allProductsDontNeedAnvisa = allProductsDeliveredByAssociation || allProductsWithSpecialBrand

        // Verifica se todos os produtos têm class_id === 6 (não precisam de documentos aprovados)
        const allProductsClassId6 = currentPackageItems.every((packageItem: any) => packageItem.class_id === 6)

        // Verifica se todos os itens do pacote (ou do pedido se sem pacote) são TESTE_GENETICO
        const itemsToCheck = currentPackageItems.length > 0 ? currentPackageItems : data?.order?.items || []
        const allProductsGeneticTest =
          itemsToCheck.length > 0 &&
          itemsToCheck.every((item: any) => (item.product_type || item.product?.type || item.type) === 'TESTE_GENETICO')

        const isPillOnly = data?.order?.summary?.is_pill_only ?? data?.order?.is_pill_only ?? false

        if (isPillOnly) {
          const paidPayment = data?.order?.payments?.find((payment: any) => payment.status === 'paid')
          const hasControlledItem = (data?.order?.items || []).some(
            (item: any) =>
              (item.product_type || item.product?.type || item.type) === 'PRODUTO' &&
              item.requires_controlled_prescription
          )

          setTimeLineStatus(buildPillOrderTimeline(packageLogs, paidPayment, hasControlledItem))
          setPackageDetails(packageDetails)
          setOrderStatus(orderStatus)
          setOrderDetails(data?.order)
          setActiveStepIndex(0)
          setStatusIndex(statusIndex)
          setIsDeliveredByAssociation(false)
          if (data?.order?.packages[PackageCount]?.statusLogs) setOrderPackage(data?.order?.packages)
          SetData(data)
          setOrderId(data?.order?.id)

          return
        }

        if (allProductsGeneticTest) {
          const paidPayment = data?.order?.payments?.find((payment: any) => payment.status === 'paid')
          const logsArray = packageLogs
          setTimeLineStatus(buildGeneticTestTimeline(logsArray, paidPayment))
          setPackageDetails(packageDetails)
          setOrderStatus(orderStatus)
          setOrderDetails(data?.order)
          setActiveStepIndex(0)
          setStatusIndex(statusIndex)
          setIsDeliveredByAssociation(false)
          if (data?.order?.packages[PackageCount]?.statusLogs) setOrderPackage(data?.order?.packages)
          SetData(data)
          setOrderId(data?.order?.id)
          return
        }

        // Filtra steps que não são necessários
        let stepsToProcess = status

        // Remove step 2 (Documentos aprovados) se todos os produtos têm class_id === 6
        if (allProductsClassId6) {
          stepsToProcess = stepsToProcess.filter((step) => step.id !== 2)
        }

        // Remove step 5 (Alfândega EUA) se o pacote nunca passou por esse status
        const hasCustomsLog = packageLogs.some((log: any) => log.status?.name === 'problems_with_customs')
        const isCurrentlyInCustoms =
          orderStatus?.name === 'problems_with_customs' || orderStatus?.status === 'problems_with_customs'
        if (!hasCustomsLog && !isCurrentlyInCustoms) {
          stepsToProcess = stepsToProcess.filter((step) => step.id !== 5)
        }

        // Remove step 7 (ANVISA) se não for necessário
        if (allProductsDontNeedAnvisa || currentPackageSupplierId == 18) {
          stepsToProcess = stepsToProcess.filter((step) => step.id !== 7)
        }

        // Recalcula activeStepIndex sobre a lista já filtrada
        let activeStepIndex = stepsToProcess.findIndex(
          (item) => item.idName.includes(orderStatus?.name) || item.idName.includes(orderStatus?.status)
        )
        if (activeStepIndex === -1) {
          const completedStepIndex = stepsToProcess.findIndex(
            (item) => item.nextStepName.includes(orderStatus?.name) || item.nextStepName.includes(orderStatus?.status)
          )
          if (completedStepIndex !== -1 && completedStepIndex < stepsToProcess.length - 1) {
            activeStepIndex = completedStepIndex + 1
          }
        }

        setTimeLineStatus(
          stepsToProcess.map((item, index) => {
            let statusCreatedAt = ''
            let shouldBeChecked = false
            let itemLabel = item.label
            let itemMessage = ''

            // STEP 1: PAGAMENTO
            if (item.id === 1) {
              const isPaid = data?.order?.payments?.some((payment: any) => payment.status === 'paid')

              if (isPaid) {
                shouldBeChecked = true
                itemLabel = 'Pagamento aprovado'
                const paidPayment = data?.order?.payments?.find((payment: any) => payment.status === 'paid')
                statusCreatedAt = paidPayment?.createdAt || ''
                itemMessage = getStatusMessage('paid', item.id, data?.order)
              } else {
                shouldBeChecked = false
                itemLabel = 'Aguardando confirmação do pagamento'
              }
            } else {
              // OUTROS STEPS: Verifica se tem log de conclusão (nextStepName)
              const completionLogs = packageLogs.filter((log: any) => item.nextStepName.includes(log.status.name))

              if (completionLogs && completionLogs.length > 0) {
                // TEM log de conclusão - step completo
                shouldBeChecked = true
                const latestLog = completionLogs.sort(
                  (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
                )[0]
                statusCreatedAt = latestLog.updatedAt
                itemMessage = getStatusMessage(latestLog.status?.name, item.id, data?.order)
              } else {
                // NÃO tem log de conclusão - verifica se está ativo OU se já passou desse step
                const isCurrentlyInThisStep =
                  item.idName.includes(orderStatus.name) || item.idName.includes(orderStatus.status)
                const isBeforeActiveStep = activeStepIndex >= 0 && index < activeStepIndex

                if (isCurrentlyInThisStep) {
                  // Step ATIVO - busca log do status atual
                  shouldBeChecked = false

                  const currentStatusLogs = packageLogs.filter(
                    (log: any) => log.status?.name === orderStatus.name || log.status?.name === orderStatus.status
                  )

                  if (currentStatusLogs && currentStatusLogs.length > 0) {
                    const newestLog = currentStatusLogs.sort((a: any, b: any) => {
                      const dateA = new Date(a.updatedAt || a.createdAt).getTime()
                      const dateB = new Date(b.updatedAt || b.createdAt).getTime()
                      return dateB - dateA
                    })[0]
                    statusCreatedAt = newestLog.updatedAt || newestLog.createdAt
                    itemMessage = getStatusMessage(orderStatus.name || orderStatus.status, item.id, data?.order)
                  } else {
                    statusCreatedAt = orderStatus?.statusData || data?.order?.status?.order_status?.updatedAt || ''
                  }
                } else if (isBeforeActiveStep) {
                  // Step ANTERIOR ao ativo - marca como completo mesmo sem log específico
                  shouldBeChecked = true
                  const logsInStep = packageLogs.filter((log: any) => item.idName.includes(log.status.name))
                  if (logsInStep && logsInStep.length > 0) {
                    const mostAdvancedLog = getMostAdvancedLog(logsInStep, item.idName)
                    if (mostAdvancedLog) {
                      statusCreatedAt = mostAdvancedLog.updatedAt || mostAdvancedLog.createdAt
                      itemMessage = getStatusMessage(mostAdvancedLog.status?.name, item.id, data?.order)
                    }
                  }
                } else {
                  // Step FUTURO - não está completo nem ativo
                  shouldBeChecked = false
                  statusCreatedAt = ''
                }
              }
            }

            // Verifica se deve mostrar o infoContent
            // Só mostra se o step estiver ativo (tem statusCreatedAt) ou completo (checked)
            const isStepActiveOrComplete = shouldBeChecked || !!statusCreatedAt

            let shouldShowInfoContent = isStepActiveOrComplete
            // Lógica do pop-up do "Pacote enviado" (step 4):
            // - Oculta se: supplier_id 18, delivery_by_association, class_id 4/6, ou brand_id 173
            // - Mostra se: brand_id 177
            if (item.id === 4) {
              const shouldHidePopup =
                currentPackageSupplierId == 18 ||
                allProductsDeliveredByAssociation ||
                allProductsAssociation ||
                (allProductsWithSpecialBrand && !allProductsWithPopupBrand) // brand_id 173

              if (shouldHidePopup) {
                shouldShowInfoContent = false
              }
            }

            return {
              ...item,
              checked: shouldBeChecked,
              isVerified: shouldBeChecked,
              label: itemLabel,
              statusCreatedAt,
              message: itemMessage,
              infoContent: shouldShowInfoContent ? item.infoContent : undefined
            }
          })
        )
        setPackageDetails(packageDetails)
        setOrderStatus(orderStatus)
        setOrderDetails(data?.order)
        setActiveStepIndex(activeStepIndex)
        setStatusIndex(statusIndex)

        const selectedPackage = PackageDetails[PackageCount] || data?.order?.packages[PackageCount]

        const isDeliveredByAssociation =
          selectedPackage?.delivered_by_association ||
          data?.order?.delivered_by_association ||
          PackageDetails[PackageCount]?.delivered_by_association ||
          false

        setIsDeliveredByAssociation(isDeliveredByAssociation)

        if (data?.order?.packages[PackageCount]?.statusLogs) {
          setOrderPackage(data?.order?.packages)
        }
        SetData(data)
        setOrderId(data?.order?.id)
      }
    } catch (e: any) {
      toast.error(e?.response?.status === 404 ? 'Pedido não encontrado' : 'Erro na busca dos detalhes do pedido')
    } finally {
      setLoading(false)
    }
  }

  const fetchOrderReceipts = async (id: number | string) => {
    try {
      const response = await api.get<{ rows: OrderReceipt[] }>(`/my-account/orders/${id}/receipts`)
      setOrderReceipts(response.data.rows ?? [])
    } catch (err) {
      setOrderReceipts([])
    }
  }

  useEffect(() => {
    if (cart_id) {
      getLastOrders()
    }
  }, [cart_id])

  useEffect(() => {
    if (isLoggedIn && orderId) {
      fetchOrderReceipts(orderId)
    }
  }, [isLoggedIn, orderId])

  const handleReorder = async () => {
    try {
      setReorderLoading(true)

      if (!orderDetails?.items || orderDetails.items.length === 0) {
        toast.error('Nenhum produto encontrado neste pedido')
        return
      }

      // Validar produtos antes de criar o carrinho
      const unavailableProducts: any[] = []
      const availableItems: any[] = []

      for (const item of orderDetails.items) {
        try {
          const { product: currentProduct } = await getProductById(item.product_id)
          const isActive = currentProduct?.status !== 'inactive'

          if (!isActive) {
            unavailableProducts.push({
              name: currentProduct?.translated_name || currentProduct?.name || item.product?.name || 'Produto',
              id: item.product_id
            })
          } else {
            availableItems.push({
              product_id: item.product_id,
              quantity: item.quantity,
              class_id: currentProduct?.class?.id || item.class_id,
              brand_id: currentProduct?.brand_id || item.brand_id
            })
          }
        } catch {
          unavailableProducts.push({
            name: item.product?.translated_name || item.product?.name || 'Produto',
            id: item.product_id
          })
        }
      }

      // Se houver produtos indisponíveis, notificar o usuário
      if (unavailableProducts.length > 0) {
        const productNames = unavailableProducts.map((p) => p.name).join(', ')
        toast.error(
          `${unavailableProducts.length === 1 ? 'O produto' : 'Os produtos'} ${productNames} ${unavailableProducts.length === 1 ? 'está' : 'estão'} indisponível${unavailableProducts.length === 1 ? '' : 'eis'} no momento`,
          { autoClose: 5000 }
        )

        // Se todos os produtos estão indisponíveis, não criar o carrinho
        if (availableItems.length === 0) {
          return
        }

        // Perguntar se deseja continuar com os produtos disponíveis
        const continuar = window.confirm(
          `${availableItems.length} ${availableItems.length === 1 ? 'produto está' : 'produtos estão'} disponível${availableItems.length === 1 ? '' : 'eis'}. Deseja continuar com ${availableItems.length === 1 ? 'ele' : 'eles'}?`
        )

        if (!continuar) {
          return
        }
      }

      // No fluxo de associação o carrinho é um order_cart marcado com as flags da
      // associação, e a página ?pagina=carrinho o recupera pelo id salvo no localStorage
      // (mesmo contrato usado ao adicionar produtos em AccountAssociations/.../Cart).
      if (isAssociationRoute) {
        const { data: createResponse } = await api.post('/order_cart', {
          items: availableItems.map((item) => ({
            ...item,
            association_site: true,
            association_id: ASSOCIATION_ID
          })),
          association_site: true,
          association_id: ASSOCIATION_ID
        })

        if (createResponse?.order?.id) {
          setAssociationOrderId(createResponse.order.id)
          toast.success('Novo carrinho criado com os produtos!')
          history.push('/AreaAssociados/MinhaConta?pagina=carrinho&subpagina=carrinho')
        } else {
          toast.error('Carrinho criado mas não foi possível abrir')
        }
        return
      }

      const payload = {
        items: availableItems
      }

      const { data: createResponse } = await api.post('/order_cart', payload)

      if (createResponse?.order?.uuid) {
        const orderUuid = createResponse.order.uuid
        toast.success('Novo carrinho criado com os produtos!')
        window.location.href = `/carrinho?cart_id=${orderUuid}`
      } else {
        toast.error('Carrinho criado mas não foi possível abrir')
      }
    } catch (error: any) {
      console.error('❌ Erro ao criar carrinho:', error)
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        'Não foi possível criar o carrinho com os produtos'
      toast.error(errorMessage)
    } finally {
      setReorderLoading(false)
    }
  }

  if (loading) return isAssociationRoute ? <GenericLoading loading={loading} /> : <LoadingContainer loading />

  function packageUpdate(packIndex: number) {
    const selectedPkg = PackageDetails[packIndex]
    const packageLogs = filterLogsForPackage(selectedPkg?.statusLogs, selectedPkg)

    if (packageLogs.length > 0 || getLatestByUpdated(selectedPkg?.statusLogs)?.status) {
      const stepsProgression = status.flatMap((s) => s.idName)
      const effectiveStatus = getEffectivePackageStatus(
        packageLogs,
        stepsProgression,
        Data?.order?.status?.order_status
      )
      let orderStat = StatusIndex > 1 ? effectiveStatus : Data?.order?.status?.order_status

      const matchingLog = packageLogs.find((log: any) => log?.status?.name === orderStat?.name)
      orderStat = {
        ...orderStat,
        statusData: matchingLog?.updatedAt || matchingLog?.createdAt || Data?.order?.status?.order_status?.updatedAt
      }

      // Verifica os produtos do pacote selecionado para filtrar os steps
      const currentPackageItems = selectedPkg?.packages_items || []
      const currentPackageSupplierId = currentPackageItems[0]?.supplier_id

      // Verifica produtos que não precisam de ANVISA por delivery_by_association, class_id
      const allProductsDeliveredByAssociation = currentPackageItems.every(
        (packageItem: any) =>
          packageItem.product?.delivery_by_association === true ||
          packageItem.class_id === 4 ||
          packageItem.class_id === 6
      )

      // Verifica produtos que não precisam de ANVISA por brand_id
      const allProductsWithSpecialBrand = currentPackageItems.every(
        (packageItem: any) =>
          BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.brand_id) ||
          BRANDS_WITH_REQUIRED_FREIGHT.includes(packageItem.product?.brand_id)
      )

      // Verifica se todos os produtos têm brand_id que deve mostrar pop-up (apenas 177)
      const allProductsWithPopupBrand = currentPackageItems.every(
        (packageItem: any) =>
          BRANDS_WITH_POPUP.includes(packageItem.brand_id) || BRANDS_WITH_POPUP.includes(packageItem.product?.brand_id)
      )

      // Verifica se todos os produtos são de associação (brand_id 176)
      const allProductsAssociation = currentPackageItems.every(
        (packageItem: any) => packageItem.brand_id === 176 || packageItem.product?.brand_id === 176
      )

      // Combina todas as condições para remover ANVISA
      const allProductsDontNeedAnvisa = allProductsDeliveredByAssociation || allProductsWithSpecialBrand

      // Verifica se todos os produtos têm class_id === 6 (não precisam de documentos aprovados)
      const allProductsClassId6 = currentPackageItems.every((packageItem: any) => packageItem.class_id === 6)

      // Verifica se todos os itens do pacote são TESTE_GENETICO
      const itemsToCheckPkg = currentPackageItems.length > 0 ? currentPackageItems : Data?.order?.items || []
      const allProductsGeneticTestPkg =
        itemsToCheckPkg.length > 0 &&
        itemsToCheckPkg.every(
          (item: any) => (item.product_type || item.product?.type || item.type) === 'TESTE_GENETICO'
        )

      const isPillOnlyPkg = Data?.order?.summary?.is_pill_only ?? Data?.order?.is_pill_only ?? false

      if (isPillOnlyPkg) {
        const paidPayment = Data?.order?.payments?.find((payment: any) => payment.status === 'paid')
        const hasControlledItemPkg = (Data?.order?.items || []).some(
          (item: any) =>
            (item.product_type || item.product?.type || item.type) === 'PRODUTO' &&
            item.requires_controlled_prescription
        )

        setTimeLineStatus(buildPillOrderTimeline(packageLogs, paidPayment, hasControlledItemPkg))
        setOrderStatus(orderStat)
        setPackageCount(packIndex)

        return
      }

      if (allProductsGeneticTestPkg) {
        const paidPayment = Data?.order?.payments?.find((payment: any) => payment.status === 'paid')
        setTimeLineStatus(buildGeneticTestTimeline(packageLogs, paidPayment))
        setOrderStatus(orderStat)
        setPackageCount(packIndex)
        return
      }

      // Filtra steps que não são necessários - começa do array original 'status'
      let stepsToProcess = status.map((step) => ({ ...step }))

      // Remove step 2 (Documentos aprovados) se todos os produtos têm class_id === 6
      if (allProductsClassId6) {
        stepsToProcess = stepsToProcess.filter((step) => step.id !== 2)
      }

      // Remove step 5 (Alfândega EUA) se o pacote nunca passou por esse status
      const hasCustomsLogPkg = packageLogs.some((log: any) => log.status?.name === 'problems_with_customs')
      const isCurrentlyInCustomsPkg =
        orderStat?.name === 'problems_with_customs' || orderStat?.status === 'problems_with_customs'
      if (!hasCustomsLogPkg && !isCurrentlyInCustomsPkg) {
        stepsToProcess = stepsToProcess.filter((step) => step.id !== 5)
      }

      // Remove step 7 (ANVISA) se não for necessário
      if (allProductsDontNeedAnvisa || currentPackageSupplierId == 18) {
        stepsToProcess = stepsToProcess.filter((step) => step.id !== 7)
      }

      // Recalcula activeStepIndex baseado nos steps filtrados
      let activeStepIndex = stepsToProcess.findIndex(
        (item) => item.idName.includes(orderStat.name) || item.idName.includes(orderStat.status)
      )

      // Se não encontrou no idName, verifica se está no nextStepName (status de conclusão)
      if (activeStepIndex === -1) {
        const completedStepIndex = stepsToProcess.findIndex(
          (item) => item.nextStepName.includes(orderStat.name) || item.nextStepName.includes(orderStat.status)
        )
        if (completedStepIndex !== -1 && completedStepIndex < stepsToProcess.length - 1) {
          activeStepIndex = completedStepIndex + 1
        }
      }

      setTimeLineStatus(
        stepsToProcess.map((item, index) => {
          let statusCreatedAt = ''
          let shouldBeChecked = false
          let itemLabel = item.label
          let itemMessage = ''

          // STEP 1: PAGAMENTO
          if (item.id === 1) {
            const isPaid = Data?.order?.payments?.some((payment: any) => payment.status === 'paid')

            if (isPaid) {
              shouldBeChecked = true
              itemLabel = 'Pagamento aprovado'
              const paidPayment = Data?.order?.payments?.find((payment: any) => payment.status === 'paid')
              statusCreatedAt = paidPayment?.createdAt || ''
              itemMessage = getStatusMessage('paid', item.id, Data?.order)
            } else {
              shouldBeChecked = false
              itemLabel = 'Aguardando confirmação do pagamento'
            }
          } else {
            // OUTROS STEPS: Verifica se tem log de conclusão (nextStepName)
            const completionLogs = packageLogs.filter((log: any) => item.nextStepName.includes(log.status.name))

            if (completionLogs && completionLogs.length > 0) {
              // TEM log de conclusão - step completo
              shouldBeChecked = true
              const latestLog = completionLogs.sort(
                (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
              )[0]
              statusCreatedAt = latestLog.updatedAt
              itemMessage = getStatusMessage(latestLog.status?.name, item.id, Data?.order)
            } else {
              // NÃO tem log de conclusão - verifica se está ativo OU se já passou desse step
              const isCurrentlyInThisStep =
                item.idName.includes(orderStat.name) || item.idName.includes(orderStat.status)
              const isBeforeActiveStep = activeStepIndex >= 0 && index < activeStepIndex

              if (isCurrentlyInThisStep) {
                // Step ATIVO - busca log do status atual
                shouldBeChecked = false

                // Primeiro tenta buscar logs do status atual específico
                const currentStatusLogs = packageLogs.filter(
                  (log: any) => log.status?.name === orderStat.name || log.status?.name === orderStat.status
                )

                if (currentStatusLogs && currentStatusLogs.length > 0) {
                  // Pega o log MAIS RECENTE do status atual
                  const newestLog = currentStatusLogs.sort((a: any, b: any) => {
                    const dateA = new Date(a.updatedAt || a.createdAt).getTime()
                    const dateB = new Date(b.updatedAt || b.createdAt).getTime()
                    return dateB - dateA
                  })[0]
                  statusCreatedAt = newestLog.updatedAt || newestLog.createdAt
                  itemMessage = getStatusMessage(orderStat.name || orderStat.status, item.id, Data?.order)
                } else {
                  // Fallback: usa data do orderStat se não tem logs
                  statusCreatedAt = orderStat?.statusData || Data?.order?.status?.order_status?.updatedAt || ''
                }
              } else if (isBeforeActiveStep) {
                // Step ANTERIOR ao ativo - marca como completo mesmo sem log específico
                shouldBeChecked = true
                // Busca o log mais AVANÇADO do step (não o mais recente por data)
                const logsInStep = packageLogs.filter((log: any) => item.idName.includes(log.status.name))
                if (logsInStep && logsInStep.length > 0) {
                  // Usa a função getMostAdvancedLog para pegar o status mais avançado na progressão
                  const mostAdvancedLog = getMostAdvancedLog(logsInStep, item.idName)
                  if (mostAdvancedLog) {
                    statusCreatedAt = mostAdvancedLog.updatedAt || mostAdvancedLog.createdAt
                    itemMessage = getStatusMessage(mostAdvancedLog.status?.name, item.id, Data?.order)
                  }
                }
              } else {
                // Step FUTURO - não está completo nem ativo
                shouldBeChecked = false
                statusCreatedAt = ''
              }
            }
          }

          // Verifica se deve mostrar o infoContent
          const isStepActiveOrComplete = shouldBeChecked || !!statusCreatedAt

          let shouldShowInfoContent = isStepActiveOrComplete
          if (item.id === 4) {
            const shouldHidePopup =
              currentPackageSupplierId == 18 ||
              allProductsDeliveredByAssociation ||
              allProductsAssociation ||
              (allProductsWithSpecialBrand && !allProductsWithPopupBrand)

            if (shouldHidePopup) {
              shouldShowInfoContent = false
            }
          }

          return {
            ...item,
            checked: shouldBeChecked,
            isVerified: shouldBeChecked,
            label: itemLabel,
            statusCreatedAt,
            message: itemMessage,
            infoContent: shouldShowInfoContent ? item.infoContent : undefined
          }
        })
      )

      setOrderStatus(orderStat)
      setActiveStepIndex(activeStepIndex)
      setPackageCount(packIndex)
    }
  }

  const validItems = orderDetails.items.filter((item: any) => (item.quantity || 0) > 0)
  const productsTotal = validItems.reduce(
    (acc: number, item: any) => acc + (item.unitPrice || 0) * (item.quantity || 0),
    0
  )
  const productDiscounts = orderDetails?.discount || 0
  const subtotal = productsTotal - productDiscounts
  const importCost = orderDetails.shipping || 145
  const noImportCost = orderDetails?.noImportCost || false
  const nationalFreight = parseFloat(orderDetails?.national_freight) || 0

  const isPillOnlyOrder: boolean = orderDetails?.summary?.is_pill_only ?? orderDetails?.is_pill_only ?? false

  const PILL_DOCUMENT_TERMINAL_STATUSES = ['dispatched', 'sented', 'delivered']
  const currentStatusName: string = orderStatus?.name || orderStatus?.status || ''
  const pillDocumentLinkForced = isPillOnlyOrder && !PILL_DOCUMENT_TERMINAL_STATUSES.includes(currentStatusName)

  const pillDocumentLinkUrl =
    isPillOnlyOrder && orderDetails?.uuid ? `/validacao-documentos/${orderDetails.uuid}` : undefined

  const total = orderDetails.amount
  const freeShippingThreshold = 2300
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - total)
  const importDiscount = orderDetails?.shippingDiscount || 0
  const finalTotal = orderDetails?.amount

  return (
    <S.ContainerContent isSmallScreen={isSmallScreen}>
      {isAssociationRoute && (
        <div className="mx-auto w-full max-w-[1400px] px-4 pb-2 pt-6 sm:px-2">
          <button
            type="button"
            onClick={handleBackToAssociationArea}
            className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50">
            <BsArrowLeft />
            Voltar para a área de associados
          </button>
        </div>
      )}
      <div
        className={`flex w-full flex-col sm:mx-2 sm:my-2 sm:max-w-full sm:justify-center md:m-3 lg:flex-row lg:gap-8 ${
          isAssociationRoute ? 'mt-2 md:mt-2' : 'mt-6 md:mt-6'
        }`}>
        <div className="bg-white p-10 sm:w-full sm:max-w-none sm:p-4 md:p-6 lg:w-2/3">
          <h1 className="mb-6 mt-3 text-xl font-semibold text-gray-800 sm:mb-3 sm:mt-2 sm:text-base md:mb-4 md:text-lg">
            Status do pedido
          </h1>
          <div className="flex flex-col gap-4 rounded-lg bg-neutral-50 p-3">
            <div className="flex flex-col gap-4">
              <Header
                orderId={cart_id}
                packages={OrderPackage}
                selectedPackageIndex={PackageCount}
                onPackageUpdate={packageUpdate}
              />
              <Separator />
              <ShippingDetails
                carrierName={
                  OrderPackage[PackageCount]?.carrier_name ||
                  (OrderAwb.length > 0 && OrderAwb[PackageCount]?.shippingCompany) ||
                  ''
                }
                trackingNumber={
                  OrderPackage[PackageCount]?.tracking_number ||
                  (OrderAwb.length > 0 && OrderAwb[PackageCount]?.trackingNumber) ||
                  ''
                }
                contactLink={linkAtendimento}
                estimatedDeliveryRangeStart={PackageDetails[PackageCount]?.estimated_delivery_range_start || null}
                estimatedDeliveryRangeEnd={PackageDetails[PackageCount]?.estimated_delivery_range_end || null}
              />
              <Separator />
            </div>
            <Timeline
              orderId={orderId}
              statusLogs={timeLineStatus}
              hideSendDocuments={
                (isWaFlowFromUrl ||
                  orderDetails?.origin === 'REENVIO' ||
                  (orderDetails?.originCheckout === 'B2C' && !isPillOnlyOrder)) &&
                !isLoggedIn
              }
              alwaysShowDocuments={pillDocumentLinkForced}
              documentLinkUrl={pillDocumentLinkUrl}
            />
          </div>
        </div>

        <div className="mt-6 sm:w-full sm:max-w-none lg:mt-0 lg:w-1/2">
          <div className="h-auto w-full rounded-xl border border-gray-200 bg-white p-8 shadow-sm sm:p-4 md:p-6">
            <div className="space-y-7 sm:space-y-4 md:space-y-5">
              <h3 className="text-lg font-semibold text-gray-900 sm:text-sm md:text-base">Resumo do Pedido</h3>

              {/* Documentos de Teste Genético */}
              {(() => {
                const geneticTestDocs = (orderDetails?.documents || [])
                  .flat()
                  .filter((doc: any) => doc.type === 'TESTE_GENETICO')
                if (geneticTestDocs.length === 0) return null
                return (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-800">Resultado do Teste Genético</h4>
                    {geneticTestDocs.map((doc: any, index: number) => (
                      <a
                        key={doc.id || index}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-700 transition-colors hover:bg-primary-100">
                        <BsFileEarmarkText size={18} className="flex-shrink-0" />
                        <span>
                          {doc.originalName || `Teste Genético${geneticTestDocs.length > 1 ? ` ${index + 1}` : ''}`}
                        </span>
                      </a>
                    ))}
                  </div>
                )
              })()}

              <div className="space-y-2 sm:space-y-1">
                <div className="flex items-center justify-between text-sm text-gray-600 sm:text-xs md:text-sm">
                  <span>Produtos ({validItems.length})</span>
                  <span>{formatReal(productsTotal)}</span>
                </div>
                {productDiscounts > 0 && (
                  <div className="flex items-center justify-between text-sm text-gray-600 sm:text-xs md:text-sm">
                    <span>Descontos nos produtos</span>
                    <span>- {formatReal(productDiscounts)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm text-gray-600 sm:text-xs md:text-sm">
                  <span>Subtotal</span>
                  <span>{formatReal(subtotal)}</span>
                </div>
                <div className="pt-4 sm:pt-2">
                  <hr className="h-[0.2px] border-none bg-gray-200" />
                </div>
                {!noImportCost && (
                  <div className="flex items-center justify-between text-sm text-gray-600 sm:text-xs md:text-sm">
                    <span>Custo de importação</span>
                    <span>{formatReal(importCost)}</span>
                  </div>
                )}
                {shouldShowNationalFreight(nationalFreight, hasQuotedNationalFreight(orderDetails)) && (
                  <div className="flex items-center justify-between text-sm text-gray-600 sm:text-xs md:text-sm">
                    <span>Frete nacional</span>
                    <span>{nationalFreight > 0 ? formatReal(nationalFreight) : FREE_NATIONAL_FREIGHT_LABEL}</span>
                  </div>
                )}
                {!noImportCost && (
                  <div className="flex items-center justify-between text-sm text-gray-600 sm:text-xs md:text-sm">
                    <span>Descontos na importação</span>
                    <span className={importDiscount > 0 ? 'text-green-600' : 'text-gray-600'}>
                      {importDiscount > 0 ? `- ${formatReal(importDiscount)}` : '- R$ 0,00'}
                    </span>
                  </div>
                )}
                <div className="pt-4 sm:pt-2">
                  <hr className="h-[0.2px] border-none bg-gray-200" />
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 pt-3 text-lg font-semibold text-gray-900 sm:pt-2 sm:text-sm md:text-base">
                  <span className="text-base font-normal text-gray-800 sm:text-xs md:text-sm">Total</span>
                  <span>{formatReal(finalTotal)}</span>
                </div>

                {orderDetails?.coupon && (
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 p-1 text-primary-600 sm:gap-2 sm:p-2">
                    <BsTicketPerforated size={24} className="sm:h-5 sm:w-5" />
                    <p className="text-base sm:text-sm md:text-sm">
                      Cupom:{' '}
                      <span className="text-gray-900">
                        {typeof orderDetails?.coupon === 'object'
                          ? orderDetails?.coupon?.name || orderDetails?.coupon?.code || 'Cupom aplicado'
                          : orderDetails?.coupon}
                      </span>
                    </p>
                  </div>
                )}
              </div>
              {validItems.map((item: any, index: number) => (
                <div
                  key={`product-${index}`}
                  className="flex items-start gap-4 rounded-lg border border-gray-200 p-4 sm:gap-2 sm:p-3 md:gap-3 md:p-3">
                  <img
                    src={item.product?.media?.[0]?.path || '/placeholder.png'}
                    alt={item.product?.name || 'Produto'}
                    className="h-32 w-24 flex-shrink-0 rounded-lg border border-gray-200 object-cover sm:h-24 sm:w-16 md:h-28 md:w-20"
                  />
                  <div className="min-w-0 flex-1 space-y-2 sm:space-y-1 md:space-y-1">
                    <h4 className="break-words font-semibold leading-tight text-gray-900 sm:text-xs md:text-sm">
                      {item.product?.translated_name || 'Nome do Produto'}
                    </h4>
                    <div className="break-words text-xs leading-tight text-neutral-500 sm:text-[10px] md:text-xs">
                      <span>Sabor: {item.product?.flavor || 'N/A'}</span>
                      <span className="mx-2">/</span>
                      <span>Quantidade: {item.quantity || 0}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-1 md:gap-1">
                      {item.product?.price && item.product?.real_price && (
                        <>
                          <span className="text-[12px] italic text-neutral-500 line-through sm:text-[10px] md:text-[11px]">
                            R$ {item.unitPrice}
                          </span>
                          {item.promotion && item.promotion.discount_type === 'percent' ? (
                            <span className="text-[12px] italic text-primary-500 sm:text-[10px] md:text-[11px]">
                              -{item.promotion?.discount_value}%
                            </span>
                          ) : (
                            <span className="text-[12px] italic text-primary-500 sm:text-[10px] md:text-[11px]">
                              - R$ {item.promotion?.discount_value}
                            </span>
                          )}
                        </>
                      )}
                      <span className="text-lg font-semibold text-neutral-800 sm:text-sm md:text-base">
                        R$ {item?.amount}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {isLoggedIn && orderReceipts.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <h3 className="mb-3 text-base font-semibold text-gray-900">Comprovantes</h3>
                <div className="flex flex-col gap-2">
                  {orderReceipts.map((receipt) => (
                    <a
                      key={receipt.id}
                      href={receipt.download_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50">
                      <span>{RECEIPT_LABELS[receipt.type]}</span>
                      <span className="text-primary">Baixar PDF</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Botão Comprar Novamente */}
            {isLoggedIn && (
              <button
                onClick={handleReorder}
                disabled={reorderLoading}
                className="mt-6 w-full rounded-lg bg-primary py-4 font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50">
                {reorderLoading ? 'Criando carrinho...' : 'Comprar Novamente'}
              </button>
            )}
          </div>
        </div>
      </div>
    </S.ContainerContent>
  )
}

export default OrderDetails
